import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  type Step,
  WalkthroughDialogComponent,
  WalkthroughNavigationHelperService,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';
import { walkthroughAssetsResolver } from '@core/walkthrough-util';

@Injectable({ providedIn: 'root' })
export class MarketplaceToolboxWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly gettingStartedFacade = inject(GettingStartedFacade);
  private readonly navigationHelperService = inject(WalkthroughNavigationHelperService);

  private readonly destroyRef = inject(DestroyRef);

  public readonly subtask = GettingStartedSubtask.DiscoverNewApps;

  private steps: Step[] = [
    {
      id: 'walkthrough-step-marketplace-toolbox-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.marketplace_toolbox.step_1.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.marketplace_toolbox.step_1.description',
          ),
          imageUrl: walkthroughAssetsResolver('marketplace_essential_tools.svg'),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.marketplace_toolbox.step_1.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-marketplace-toolbox-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.marketplace_toolbox.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.marketplace_toolbox.step_2.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.marketplace_toolbox.step_2.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 24, offsetY: 0 },
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(state, this.subtask, ['marketplace']);
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }
  private finishHandler(): void {
    this.clearDialogOutlet();
  }

  private clearDialogOutlet(): void {
    this.router
      .navigate([{ outlets: { dialog: null } }])
      .then(() => this.router.navigate(['marketplace', 'collections', 'free'])); // TODO walkthrough change collection id
  }
}
